'use client';

import { useState, useEffect, useRef } from 'react';
import { useIntl } from 'react-intl';
import { Checkbox } from '@packages/shared/src/ui/Checkbox/Checkbox';
import { Divider } from '@packages/shared/src/ui/Divider/Divider';
import { Image } from '@packages/shared/src/ui/Image/Image';
import {
  Accordion,
  AccordionItem,
  AccordionSummary,
  AccordionDetails,
} from '@packages/shared/src/ui/Accordion/Accordion';
import { StatusChip } from '@packages/shared/src/ui/StatusChip/StatusChip';
import { CircularProgress } from '@packages/shared/src/ui/CircularProgress/CircularProgress';
import { Notification } from '@packages/shared/src/ui/Notification/Notification';
import { useI18n } from '@packages/shared/src/hooks/useI18n/useI18n';
import { useAuthMutation } from '@packages/shared/src/hooks/useAuthMutation/useAuthMutation';
import { usePriceFormater } from '@packages/shared/src/hooks/usePriceFormater/usePriceFormater';
import type { FragmentType } from '@packages/gql/generated/shopping';
import { getFragmentData } from '@packages/gql/generated/shopping';
import { AplusFragmentFragmentDoc } from '@packages/gql/generated/shopping/AplusFragmentFragmentDoc';
import { ToggleServiceProductDocument } from '@packages/gql/generated/shopping/ToggleServiceProductDocument';
import type { AplusFragmentFragment } from '@packages/gql/generated/shopping/graphql';
import { BasketFragmentFragmentDoc } from '@packages/gql/generated/shopping/BasketFragmentFragmentDoc';
import { LineItemFragmentFragmentDoc } from '@packages/gql/generated/shopping/LineItemFragmentFragmentDoc';
import { serviceItemsMessages } from './messages';

export const ServiceItems = ({
  aplusData,
  pli,
}: {
  aplusData: FragmentType<typeof AplusFragmentFragmentDoc>;
  pli: string;
}) => {
  const { formatMessage } = useIntl();
  const formatPrice = usePriceFormater('ATB');
  const { locale } = useI18n();
  const [loadingItem, setLoadingItem] = useState<string | null>(null);
  const [errorNotification, setErrorNotification] = useState(false);
  const refTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const fragmentData = getFragmentData(AplusFragmentFragmentDoc, aplusData);
  const { clusters } = fragmentData || {};
  const [mutationState, toggleServiceProduct] = useAuthMutation(ToggleServiceProductDocument);
  const [state, setState] = useState<AplusFragmentFragment['clusters']>(clusters);
  const [accordionValue, setAccordionValue] = useState('services');

  const onSelectItem = async (serviceUuid: string) => {
    try {
      setLoadingItem(serviceUuid);
      if (!mutationState.fetching) {
        const result = await toggleServiceProduct({ locale, lineItemId: pli, serviceUuid });
        if (result && result.data) {
          const basketFragment = getFragmentData(
            BasketFragmentFragmentDoc,
            result.data.toggleServiceProduct,
          );
          basketFragment.lineItems.forEach((lineItem) => {
            const lineItemFragment = getFragmentData(LineItemFragmentFragmentDoc, lineItem);
            if (lineItemFragment.id === pli) {
              const aplusFragment = getFragmentData(
                AplusFragmentFragmentDoc,
                lineItemFragment.aplus,
              );
              setState(aplusFragment!.clusters);
            }
          });
        }
      }
    } catch {
      setErrorNotification(true);
      refTimer.current = setTimeout(() => {
        setErrorNotification(false);
      }, 5000);
    } finally {
      setLoadingItem(null);
    }
  };

  useEffect(
    () => () => {
      if (refTimer.current !== null) {
        window.clearTimeout(refTimer.current);
      }
    },
    [],
  );

  return (
    <>
      {errorNotification && (
        <Notification severity="error">
          {formatMessage(serviceItemsMessages.serviceItemsErrorMessage)}
        </Notification>
      )}
      <Accordion type="single" collapsible value={accordionValue} onValueChange={setAccordionValue}>
        <AccordionItem value="services" noStyle>
          <AccordionSummary icon="icon-emp-arrow-medium-down" className="p-0">
            <div className="flex items-center gap-2">
              <StatusChip color="success" className="text-size-body-xs h-2.5 uppercase">
                {formatMessage(serviceItemsMessages.chipLabel)}
              </StatusChip>
              <span className="text-size-body-sm">
                {formatMessage(serviceItemsMessages.serviceItemsHeadline)}
              </span>
            </div>
          </AccordionSummary>
          <AccordionDetails className="py-0">
            <Divider className="mb-1" />
            <div className="divide-color-separator flex flex-col gap-1 divide-y">
              {state.map(({ label, services }) => (
                <div key={label} className="flex flex-col gap-2 pb-1">
                  <span className="text-size-body-xs font-semibold uppercase">{label}</span>
                  {services.map(({ uuid, name, price, serviceIcon, included, selected }) => (
                    <div key={uuid} className="flex flex-row items-start gap-2">
                      <div className="flex h-4 w-4 items-center justify-center">
                        {/* eslint-disable-next-line no-nested-ternary */}
                        {included ? (
                          <span className="icon-emp-checkmark text-color-success h-4 w-4" />
                        ) : loadingItem === uuid ? (
                          <CircularProgress className="size-3" />
                        ) : (
                          <Checkbox
                            checked={selected || false}
                            onChange={() => onSelectItem(uuid)}
                          />
                        )}
                      </div>
                      <div className="flex flex-1 cursor-pointer flex-row items-start gap-2">
                        <div>
                          <Image src={`${serviceIcon}.svg`} alt={name!} width={30} height={30} />
                        </div>
                        <div className="flex flex-1 flex-col">
                          <div className="flex flex-row justify-between gap-1">
                            <span data-testid="service-name" className="text-size-body-sm">
                              {name}
                            </span>
                            {included ? (
                              <span className="text-size-body-sm text-color-success font-semibold">
                                {formatMessage(serviceItemsMessages.serviceItemsInclusive)}
                              </span>
                            ) : (
                              <span className="text-size-body-sm font-semibold">
                                +{formatPrice(price?.value || 0, price?.currency)}
                              </span>
                            )}
                          </div>
                          <span className="text-size-body-xs text-color-text-darktransparent">
                            {formatMessage(serviceItemsMessages.serviceItemsMoreInfo)}
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          </AccordionDetails>
        </AccordionItem>
      </Accordion>
    </>
  );
};
